/**
 * Post-sign-in redirect target handling. Carries the page a signed-out user asked for across the
 * sign-in flow so they land on it afterwards, in every auth mode. The target rides in
 * sessionStorage, which survives the same-tab round trip to the IDP and back to `/auth/callback`
 * that federated and direct-oidc make and that drops any in-memory or query-string value.
 */

import { ROUTES } from "./constants"

const STORAGE_KEY = "cdr.postLoginRedirect"

/**
 * Query-string parameter the backend uses in grant/notification email links
 * (`/login?redirect=/dtas/{id}`) and that ProtectedRoute appends when bouncing a signed-out user.
 */
export const REDIRECT_PARAM = "redirect"

/**
 * The login URL that will send the user to `target` after they sign in. Keeps the redirect wire
 * format (param name and encoding) owned by this module, matching what the readers here expect.
 * Returns a bare `/login` when the target is the home route, since landing there is already the
 * default after sign-in and a `?redirect=/` would be pointless clutter.
 */
export const buildLoginUrl = (target: string): string =>
  target === ROUTES.HOME
    ? ROUTES.LOGIN
    : `${ROUTES.LOGIN}?${REDIRECT_PARAM}=${encodeURIComponent(target)}`

/**
 * Whether a path is safe to redirect to after sign-in: a same-origin relative path only. Rejects
 * scheme-relative (`//host`) and backslash (`/\host`) forms that a browser could resolve to another
 * origin, closing the open-redirect hole.
 */
export const isSafeRedirectPath = (path: string | null | undefined): path is string =>
  typeof path === "string" &&
  path.startsWith("/") &&
  !path.startsWith("//") &&
  !path.startsWith("/\\")

/**
 * The safe redirect target from a URL's query params, or null when absent or unsafe. Keeps the read
 * half of the wire format (param name and safety check) owned by this module, mirroring
 * {@link buildLoginUrl} on the write side.
 */
export const readRedirectParam = (params: URLSearchParams): string | null => {
  const path = params.get(REDIRECT_PARAM)
  return isSafeRedirectPath(path) ? path : null
}

/**
 * Record where to send the user after sign-in. Stores a safe path and clears any stale target
 * otherwise, so a plain visit to `/login` lands on the default home page rather than an abandoned
 * target from an earlier flow.
 */
export const storeRedirectTarget = (path: string | null | undefined): void => {
  if (isSafeRedirectPath(path)) {
    sessionStorage.setItem(STORAGE_KEY, path)
  } else {
    sessionStorage.removeItem(STORAGE_KEY)
  }
}

/**
 * Read and clear the stored redirect target. Returns the path if one was stored and still safe,
 * otherwise null. Clearing on read keeps the target single-use, so a user who cannot access it
 * falls back to normal landing behavior with no redirect loop.
 */
export const consumeRedirectTarget = (): string | null => {
  const path = sessionStorage.getItem(STORAGE_KEY)
  sessionStorage.removeItem(STORAGE_KEY)
  return isSafeRedirectPath(path) ? path : null
}
